import { API_CONFIG } from '../../config/env';
import { fetchJsonWithCache, withRetry } from './network';
import { createTranslateError } from './error';
import { debug, info } from './logger';
import { GoogleTranslateResponse, M2M100Response, TranslateService } from './types';

/**
 * 翻译文本
 * @param text 要翻译的文本
 * @param sourceLanguage 源语言
 * @param targetLanguage 目标语言
 * @param service 翻译服务
 */
export async function translateText(
  text: string,
  sourceLanguage: string = 'zh-CN',
  targetLanguage: string = 'ja',
  service: TranslateService = TranslateService.Google
): Promise<string> {
  if (!text || text.trim() === '') {
    return '';
  }

  // 缓存键
  const cacheKey = `translate_${service}_${sourceLanguage}_${targetLanguage}_${text}`;

  try {
    let url: string;
    let result: string;

    switch (service) {
      case TranslateService.M2M100:
        url = `https://m2m100.whoops.space/?text=${encodeURIComponent(text)}&source_language=zh&target_language=ja&secret=q1w2e3r4`;

        const m2mData = await withRetry(() =>
          fetchJsonWithCache<M2M100Response>(url, {}, cacheKey)
        );

        result = m2mData.text;
        break;

      case TranslateService.Google:
      default:
        url = `${API_CONFIG.TRANSLATE_API}/translate_a/single?client=gtx&sl=${sourceLanguage}&tl=${targetLanguage}&dt=t&q=${encodeURIComponent(text)}`;

        const googleData = await withRetry(() =>
          fetchJsonWithCache<GoogleTranslateResponse>(url, {}, cacheKey)
        );

        if (!googleData || !googleData[0] || !googleData[0][0] || !googleData[0][0][0]) {
          throw createTranslateError('无效的翻译响应格式', { service, response: googleData });
        }

        result = googleData[0][0][0];
        break;
    }

    info(`翻译完成: "${text}" => "${result}"`);
    return result;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw createTranslateError(`翻译失败: ${error.message}`, { text, sourceLanguage, targetLanguage, service });
    }
    throw createTranslateError(`翻译失败: ${String(error)}`, { text, sourceLanguage, targetLanguage, service });
  }
}

/**
 * 将简体中文转换为繁体中文
 * @param text 简体中文文本
 */
export const convertToTraditional = async (text: string): Promise<string> => {
  if (!text || text.trim() === '') {
    return '';
  }

  try {
    return await translateText(text, 'zh-CN', 'zh-TW', TranslateService.Google);
  } catch (error: unknown) {
    debug('繁体转换失败', error);
    return text; // 转换失败时返回原文
  }
};